/**
 * Reliable inline-SVG illustrations per SKU.category.
 *
 * Why SVG, not Unsplash CDN: hand-picked Unsplash photo IDs kept
 * 404-ing or pointing at wrong content (user-spotted at iter 7c6).
 * SVG illustrations are deterministic, lightweight, never fail, and
 * keep the renderer 100% ontology-driven: real product photos come
 * from C.0 Catalog Importer populating SKU.attrs.photoUrl with the
 * live schema.org/Product.image URL.
 *
 * Apple-style — clean line + soft fill in our cream/ink/foyr palette.
 */
import type { GraphNode } from "../types/ontology";

const FILL = "%23FBF5E9";       // cream-50
const STROKE = "%23141612";     // ink-950
const ACCENT = "%23C95C1A";     // foyr
const SOFT = "%237a766f";       // ink-500
const BG = "%23ECE7DC";         // cream-200

function dataUri(svg: string): string {
  // Compact + safe — viewBox-only SVG, sized to whatever container by CSS.
  return `data:image/svg+xml;utf8,${svg.replace(/\n+/g, " ").replace(/\s{2,}/g, " ").trim()}`;
}

const SVG_BY_CATEGORY: Readonly<Record<string, string>> = {
  Sofa: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='30' y='60' width='180' height='50' rx='10' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <rect x='30' y='40' width='180' height='30' rx='8' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <line x1='90' y1='60' x2='90' y2='110' stroke='${SOFT}' stroke-width='1'/>
    <line x1='150' y1='60' x2='150' y2='110' stroke='${SOFT}' stroke-width='1'/>
    <rect x='26' y='100' width='8' height='18' fill='${STROKE}'/>
    <rect x='206' y='100' width='8' height='18' fill='${STROKE}'/>
  </svg>`),
  Bed: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='30' y='40' width='180' height='75' rx='6' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <rect x='30' y='40' width='180' height='18' fill='${SOFT}' opacity='0.25'/>
    <rect x='50' y='62' width='60' height='22' rx='3' fill='${FILL}' stroke='${SOFT}' stroke-width='1'/>
    <rect x='130' y='62' width='60' height='22' rx='3' fill='${FILL}' stroke='${SOFT}' stroke-width='1'/>
    <text x='120' y='130' text-anchor='middle' font-family='-apple-system,Inter,sans-serif' font-size='11' fill='${SOFT}'>Bed</text>
  </svg>`),
  Dining: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <ellipse cx='120' cy='75' rx='75' ry='34' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <circle cx='38' cy='75' r='9' fill='${FILL}' stroke='${STROKE}' stroke-width='1.5'/>
    <circle cx='202' cy='75' r='9' fill='${FILL}' stroke='${STROKE}' stroke-width='1.5'/>
    <circle cx='75' cy='30' r='9' fill='${FILL}' stroke='${STROKE}' stroke-width='1.5'/>
    <circle cx='165' cy='30' r='9' fill='${FILL}' stroke='${STROKE}' stroke-width='1.5'/>
    <circle cx='75' cy='120' r='9' fill='${FILL}' stroke='${STROKE}' stroke-width='1.5'/>
    <circle cx='165' cy='120' r='9' fill='${FILL}' stroke='${STROKE}' stroke-width='1.5'/>
  </svg>`),
  Table: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='60' y='55' width='120' height='40' rx='4' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <rect x='65' y='95' width='6' height='25' fill='${STROKE}'/>
    <rect x='169' y='95' width='6' height='25' fill='${STROKE}'/>
  </svg>`),
  Island: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='40' y='50' width='160' height='60' rx='5' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <circle cx='80' cy='75' r='6' fill='none' stroke='${ACCENT}' stroke-width='2'/>
    <circle cx='110' cy='75' r='6' fill='none' stroke='${ACCENT}' stroke-width='2'/>
    <circle cx='80' cy='95' r='6' fill='none' stroke='${ACCENT}' stroke-width='2'/>
    <circle cx='110' cy='95' r='6' fill='none' stroke='${ACCENT}' stroke-width='2'/>
    <rect x='150' y='65' width='40' height='32' rx='3' fill='none' stroke='${SOFT}' stroke-width='1'/>
  </svg>`),
  Wardrobe: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='70' y='20' width='100' height='110' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <line x1='120' y1='22' x2='120' y2='128' stroke='${SOFT}' stroke-width='1.5'/>
    <circle cx='115' cy='75' r='2' fill='${STROKE}'/>
    <circle cx='125' cy='75' r='2' fill='${STROKE}'/>
  </svg>`),
  Counter: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='30' y='60' width='180' height='25' rx='3' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <rect x='100' y='65' width='40' height='15' rx='2' fill='none' stroke='${SOFT}' stroke-width='1'/>
    <line x1='120' y1='66' x2='120' y2='80' stroke='${SOFT}' stroke-width='1'/>
  </svg>`),
  TV: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='40' y='35' width='160' height='80' rx='4' fill='${STROKE}'/>
    <rect x='48' y='42' width='144' height='66' rx='2' fill='${SOFT}' opacity='0.4'/>
    <rect x='100' y='118' width='40' height='6' fill='${STROKE}'/>
  </svg>`),
  Study: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='40' y='50' width='160' height='30' rx='3' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <rect x='100' y='85' width='40' height='4' fill='${STROKE}'/>
    <rect x='105' y='89' width='3' height='25' fill='${STROKE}'/>
    <rect x='132' y='89' width='3' height='25' fill='${STROKE}'/>
    <circle cx='120' cy='105' r='12' fill='${FILL}' stroke='${STROKE}' stroke-width='1.5'/>
  </svg>`),
  Bar: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='70' y='30' width='100' height='90' rx='4' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <line x1='70' y1='75' x2='170' y2='75' stroke='${SOFT}' stroke-width='1.5'/>
    <rect x='90' y='40' width='10' height='25' fill='none' stroke='${ACCENT}' stroke-width='1.5'/>
    <rect x='110' y='40' width='10' height='25' fill='none' stroke='${ACCENT}' stroke-width='1.5'/>
    <rect x='130' y='40' width='10' height='25' fill='none' stroke='${ACCENT}' stroke-width='1.5'/>
  </svg>`),
  Light: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <line x1='120' y1='10' x2='120' y2='35' stroke='${STROKE}' stroke-width='1.5'/>
    <path d='M 85 35 L 155 35 L 145 80 L 95 80 Z' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <circle cx='120' cy='95' r='10' fill='${ACCENT}' opacity='0.85'/>
    <circle cx='120' cy='95' r='3' fill='${FILL}'/>
  </svg>`),
  Storage: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='60' y='25' width='120' height='100' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <line x1='60' y1='60' x2='180' y2='60' stroke='${SOFT}' stroke-width='1'/>
    <line x1='60' y1='95' x2='180' y2='95' stroke='${SOFT}' stroke-width='1'/>
    <circle cx='170' cy='42' r='2' fill='${STROKE}'/>
    <circle cx='170' cy='77' r='2' fill='${STROKE}'/>
    <circle cx='170' cy='112' r='2' fill='${STROKE}'/>
  </svg>`),
  Chair: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='90' y='40' width='60' height='15' rx='2' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <rect x='90' y='60' width='60' height='8' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <line x1='95' y1='68' x2='95' y2='120' stroke='${STROKE}' stroke-width='2'/>
    <line x1='145' y1='68' x2='145' y2='120' stroke='${STROKE}' stroke-width='2'/>
  </svg>`),
  Rug: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='40' y='35' width='160' height='80' rx='3' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <rect x='52' y='47' width='136' height='56' rx='2' fill='none' stroke='${ACCENT}' stroke-width='1' stroke-dasharray='4 2'/>
    <rect x='62' y='57' width='116' height='36' rx='2' fill='none' stroke='${SOFT}' stroke-width='1'/>
  </svg>`),
  Item: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='80' y='40' width='80' height='60' rx='4' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <text x='120' y='125' text-anchor='middle' font-family='-apple-system,Inter,sans-serif' font-size='11' fill='${SOFT}'>Item</text>
  </svg>`),
  Material: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <defs>
      <linearGradient id='wood' x1='0' x2='0' y1='0' y2='1'>
        <stop offset='0' stop-color='%23b88b5a'/>
        <stop offset='1' stop-color='%23845a36'/>
      </linearGradient>
    </defs>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='20' y='20' width='200' height='100' rx='6' fill='url(%23wood)' stroke='${STROKE}' stroke-width='1.5'/>
    <line x1='20' y1='45' x2='220' y2='45' stroke='${STROKE}' stroke-width='0.4' opacity='0.4'/>
    <line x1='20' y1='70' x2='220' y2='70' stroke='${STROKE}' stroke-width='0.4' opacity='0.4'/>
    <line x1='20' y1='95' x2='220' y2='95' stroke='${STROKE}' stroke-width='0.4' opacity='0.4'/>
    <text x='120' y='133' text-anchor='middle' font-family='-apple-system,Inter,sans-serif' font-size='9' fill='${SOFT}' letter-spacing='1.5'>MATERIAL</text>
  </svg>`),
  Texture: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='20' y='20' width='200' height='100' rx='6' fill='${FILL}' stroke='${STROKE}' stroke-width='1.5'/>
    <circle cx='60' cy='50' r='3' fill='${SOFT}' opacity='0.35'/>
    <circle cx='100' cy='75' r='2.5' fill='${SOFT}' opacity='0.35'/>
    <circle cx='150' cy='45' r='3.2' fill='${SOFT}' opacity='0.35'/>
    <circle cx='185' cy='90' r='2.8' fill='${SOFT}' opacity='0.35'/>
    <circle cx='80' cy='100' r='2.6' fill='${SOFT}' opacity='0.35'/>
    <circle cx='135' cy='95' r='2.4' fill='${SOFT}' opacity='0.35'/>
    <circle cx='195' cy='40' r='2.8' fill='${SOFT}' opacity='0.35'/>
    <text x='120' y='133' text-anchor='middle' font-family='-apple-system,Inter,sans-serif' font-size='9' fill='${SOFT}' letter-spacing='1.5'>TEXTURE</text>
  </svg>`),
  Art: dataUri(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 240 140'>
    <rect width='240' height='140' fill='${BG}'/>
    <rect x='50' y='25' width='140' height='90' rx='2' fill='${FILL}' stroke='${STROKE}' stroke-width='2'/>
    <rect x='58' y='33' width='124' height='74' fill='%23dfe8d8' stroke='none'/>
    <path d='M70 95 Q90 60 110 80 T160 70 L170 95 Z' fill='${ACCENT}' opacity='0.55'/>
    <circle cx='150' cy='52' r='8' fill='%23f3d77d' opacity='0.7'/>
    <text x='120' y='130' text-anchor='middle' font-family='-apple-system,Inter,sans-serif' font-size='9' fill='${SOFT}' letter-spacing='1.5'>ART</text>
  </svg>`),
};

const LABEL_HINTS: ReadonlyArray<readonly [RegExp, string]> = [
  [/\b(sofa|couch|sectional|loveseat|settee)\b/i, "Sofa"],
  [/\b(bunk|bed|mattress|king|queen|cot)\b/i, "Bed"],
  [/\b(dining|dinette)\b/i, "Dining"],
  [/\b(island|hob|range|cooktop)\b/i, "Island"],
  [/\b(wardrobe|almirah|closet)\b/i, "Wardrobe"],
  [/\b(countertop|counter|worktop|quartz)\b/i, "Counter"],
  [/\b(tv|television)\b/i, "TV"],
  [/\b(desk|study|workstation)\b/i, "Study"],
  [/\b(bar)\b/i, "Bar"],
  [/\b(chandelier|pendant|sconce|lamp|light)\b/i, "Light"],
  [/\b(rack|shelf|shelv|bookcase|storage|cabinet)\b/i, "Storage"],
  [/\b(chair|stool|ottoman|bench|seat)\b/i, "Chair"],
  [/\b(rug|carpet)\b/i, "Rug"],
  [/\b(coffee|side|center|console|table)\b/i, "Table"],
  [/\b(painting|art|print|frame|poster|mural|iris)\b/i, "Art"],
  [/^TXT(WOOD|FLOR)|wood\s*texture|floor\s*texture|laminate|veneer/i, "Material"],
  [/^TXT|texture|fabric|leather|carpeting|wallpaper/i, "Texture"],
  [/^RPBMP|^CUSTM|appliance|fixture|sanitary/i, "Item"],
];

function inferCategory(label: string): string {
  for (const [re, cat] of LABEL_HINTS) {
    if (re.test(label)) return cat;
  }
  return "Item";
}

/** Resolve a SKU node to a reliable illustration.
 *  1. Read typed SKU.category attribute (set by C.0 importer / ontology).
 *  2. Otherwise infer category from label tokens (sofa, wardrobe, TXTWOOD…).
 *  3. Fall back to "Item" silhouette.
 *  Imported-catalog SKUs from Foyr Neo arrive without a category attr;
 *  inference keeps the visual surface meaningful until C.0 round-trips the
 *  category back into the ontology. */
export function illustrationForSku(sku: GraphNode): string {
  const declared = typeof sku.attrs["category"] === "string" ? (sku.attrs["category"] as string) : "";
  const cat = declared.length > 0 ? declared : inferCategory(sku.label);
  return SVG_BY_CATEGORY[cat] ?? SVG_BY_CATEGORY["Item"]!;
}
